import { useEffect, useState } from 'react'
import { PublicQuizAPI, AssessmentSummary } from '../../apis/PublicQuizAPI'
import { qcls, qfonts } from './quizTheme'

interface Props {
  name: string
  email: string
  role: string
  onStart: (attemptId: string, isResuming: boolean, assessmentName: string, questionCap: number | null) => void
  onBack: () => void
}

export default function SelectQuizPage({ name, email, role, onStart, onBack }: Props) {
  const [assessments, setAssessments] = useState<AssessmentSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState<string | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    PublicQuizAPI.listAssessments()
      .then(setAssessments)
      .catch(() => setError('Could not load assessments. Please try again.'))
      .finally(() => setLoading(false))
  }, [])

  const handleSelect = async (slug: string, assessmentName: string) => {
    setError('')
    setStarting(slug)
    const firstName = name.trim().split(/\s+/)[0] ?? ''
    const lastName = name.trim().split(/\s+/).slice(1).join(' ')
    try {
      const [result, info] = await Promise.all([
        PublicQuizAPI.start({ name, firstName, lastName, email, role: role || undefined, assessmentSlug: slug }),
        PublicQuizAPI.fetchInfo(slug).catch(() => ({ questionCap: null })),
      ])
      onStart(result.attemptId, result.isResuming, assessmentName, info.questionCap ?? null)
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Something went wrong. Please try again.')
      setStarting(null)
    }
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row md:h-screen md:overflow-hidden">
      {/* ── Left: brand panel ── */}
      <div
        className="md:w-5/12 md:self-stretch flex flex-col items-center px-10 pt-16 relative"
        style={{
          backgroundImage: "linear-gradient(rgba(0,0,0,0.72), rgba(0,0,0,0.72)), url('/images/hero-banner-naked.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Yellow top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-brand-yellow" />

        <div className="flex-1" style={{ maxHeight: '15vh' }} />

        {/* Logo */}
        <div className="flex items-center gap-3 mb-14">
          <div className="w-10 h-10 bg-brand-yellow rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-black font-black text-xl leading-none">D</span>
          </div>
          <span className="text-white/80 text-xs uppercase tracking-[0.2em] font-semibold">
            Digital Competence
          </span>
        </div>

        <div className="pb-10">
          <h1 className={`text-5xl font-black text-white leading-[1.1] mb-6 ${qfonts.heading}`}>
            Select your<br />
            <span className="text-brand-yellow">Assessment</span>
          </h1>
          <p className={`text-white/70 text-sm leading-relaxed max-w-[22rem] mb-12 ${qfonts.body}`}>
            Choose the assessment that matches your context. Each one is tailored to a specific scope or audience.
          </p>
        </div>

        <div className="flex-1" style={{ maxHeight: '20vh' }} />

        <div className="flex w-full h-0 md:h-10 overflow-hidden items-center justify-center flex-shrink-0">
          <p className="text-white/50 text-xs">
            &copy; {new Date().getFullYear()} UNSW. All rights reserved.
          </p>
        </div>
      </div>

      {/* ── Mobile divider ── */}
      <div className="md:hidden h-2 bg-black flex-shrink-0" />

      {/* ── Right: selection panel ── */}
      <div className="flex-1 flex flex-col px-8 py-14 bg-white md:[border-left:8px_solid_black] overflow-y-auto">
        <div className="w-full max-w-[560px] mx-auto">
          <div className="mb-8">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-black transition-colors mb-6"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
              </svg>
              Back
            </button>
            <h2 className="text-[32px] font-black text-black mb-2 font-quiz-heading">Choose your test</h2>
            <p className="text-gray-400 text-sm">
              Hi <span className="font-semibold text-black">{name.split(' ')[0]}</span> — what do you want to see a report on today?
            </p>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-16">
              <svg className={`${qcls.spinner} w-8 h-8 text-brand-yellow`} fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          )}

          {!loading && assessments.length === 0 && !error && (
            <p className="text-gray-500 text-sm">No assessments are currently available.</p>
          )}

          {error && (
            <div className={qcls.errorBox}>
              <svg className="w-[22px] h-[22px] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              {error}
            </div>
          )}

          {!loading && assessments.length > 0 && (
            <ul className="space-y-3">
              {assessments.map((a) => {
                const isStarting = starting === a.slug
                return (
                  <li key={a.slug}>
                    <button
                      disabled={starting !== null}
                      onClick={() => handleSelect(a.slug, a.name)}
                      className={[
                        'w-full text-left px-5 py-4 rounded-lg border-2 transition-all',
                        'hover:border-brand-yellow hover:bg-brand-yellow/5',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        isStarting ? 'border-brand-yellow bg-brand-yellow/10' : 'border-gray-200 bg-white',
                      ].join(' ')}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-black text-black text-sm">{a.name}</p>
                          {a.shortDescription && (
                            <p className="text-gray-700 text-sm mt-1.5 font-medium">{a.shortDescription}</p>
                          )}
                        </div>
                        {isStarting ? (
                          <svg className={`${qcls.spinner} w-5 h-5 text-black flex-shrink-0`} fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5 text-gray-300 flex-shrink-0 group-hover:text-brand-yellow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        )}
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Footer — mobile only */}
      <div className="md:hidden h-12 flex items-center justify-center bg-black">
        <p className="text-white/40 text-xs">
          &copy; {new Date().getFullYear()} UNSW. All rights reserved.
        </p>
      </div>
    </div>
  )
}
